import {useEffect, useState} from "react";
import {useMessages} from "i18n";
import {countdownDisplay, countdownTickDelay} from "./countdownParts";
import {CountdownRoot, Digit, PlainCountdown, RollingDigits, Unit, UnitDigits, UnitLabel} from "./RollingCountdown.styles";

/**
 * Only this leaf ticks. The containing card and its minute-cadence live region keep their own cadence.
 *
 * `endedLabel` is the word it ends on, for a deadline that is not a link expiring: a request ends "Closed"
 * (kitecorp/transferify-ui#3140). Left out, it ends "Expired", as a transfer's link does. `onEnded` is told once
 * it shows that word, so a caller can change what it says around it (kitecorp/transferify-ui#3176).
 */
export function RollingCountdown({expiresAtMs, endedLabel, onEnded}: {
	expiresAtMs: number;
	endedLabel?: string;
	onEnded?: () => void;
}) {
	const {download} = useMessages();
	const [nowMs, setNowMs] = useState(Date.now);

	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | undefined;
		const tick = () => {
			const now = Date.now();
			setNowMs(now);
			if (countdownTickDelay(expiresAtMs - now) === null) clearInterval(interval);
		};
		tick();
		const delay = countdownTickDelay(expiresAtMs - Date.now());
		if (delay === null) return;
		// Align once to the deadline's fractional second; always read the wall clock after a delayed tick.
		const first = setTimeout(() => {
			interval = setInterval(tick, 1_000);
			tick();
		}, delay);
		return () => {
			clearTimeout(first);
			clearInterval(interval);
		};
	}, [expiresAtMs]);

	const copy = download.presence.countdown;
	const display = countdownDisplay(expiresAtMs - nowMs, endedLabel === undefined ? copy : {...copy, expired: endedLabel});
	const ended = display.units.length === 0;
	useEffect(() => {
		if (ended) onEnded?.();
	}, [ended, onEnded]);
	return (
		<CountdownRoot role="timer" aria-live="off" aria-label={display.text}>
			{display.units.length === 0 ? <span aria-hidden="true">{display.text}</span> : <>
				<RollingDigits aria-hidden="true" data-countdown-digits>
					{display.units.map(({unit, digits, word}) => (
						<Unit key={unit} data-countdown-unit={unit}>
							<UnitDigits>
								{digits.map((digit, index) => <Digit key={index} $value={digit} data-countdown-digit/>) }
							</UnitDigits>
							<UnitLabel>{word}</UnitLabel>
						</Unit>
					))}
				</RollingDigits>
				<PlainCountdown aria-hidden="true" data-countdown-plain>{display.text}</PlainCountdown>
			</>}
		</CountdownRoot>
	);
}
